/**
 * colors.ts — per-project color palette (mirrors the main client's `colors.ts`
 * placeholders, D-042). Used for the project color stripe + ambient card tints.
 *
 * These hex values are placeholders pending the visual design pass (D-OPEN-1).
 * Keep them in sync with `client/src/lib/colors.ts` so portal + main app
 * read as visually one product.
 */
export const COLOR_TAGS = ['amber', 'sage', 'rust', 'teal', 'slate', 'plum', 'wheat'] as const;
export type ColorTag = (typeof COLOR_TAGS)[number];

export const COLOR_HEX: Record<ColorTag, string> = {
  amber: '#d97706',
  sage:  '#3f6b3f',
  rust:  '#a13a17',
  teal:  '#0e7490',
  slate: '#475569',
  plum:  '#6b21a8',
  wheat: '#a8a29e',
};

export function colorHexFor(tag: string | null | undefined): string {
  if (!tag) return COLOR_HEX.amber;
  return (COLOR_HEX as Record<string, string>)[tag] || COLOR_HEX.amber;
}

/**
 * Status semantic color — maps a phase/task/permit status string to a
 * red/amber/green vocabulary. Matches ARCH §11 + D-046.
 */
export function statusSemantic(status: string | null | undefined): 'success' | 'warning' | 'danger' | 'neutral' {
  if (!status) return 'neutral';
  const s = status.toLowerCase();
  if (/(complete|done|approved|stamped|issued|executed)/.test(s)) return 'success';
  if (/(in progress|under review|submitted|in preparation|pending|in process|revisions)/.test(s)) return 'warning';
  if (/(blocked|denied|expired|withdrawn|missing|on hold|issue)/.test(s)) return 'danger';
  return 'neutral';
}
